import * as React from 'react'

import { Alert, Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import { FormikProvider } from 'formik'

import Modal from 'modules/core/components/Modal'
import Sidepanel from 'modules/core/components/Sidepanel'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EvaluationFormEditor } from 'modules/ats/components/EvaluationForm/components/EvaluationFormEditor'
import {
  buildEmptyQuestion,
  useEvaluationFormForm,
} from 'modules/ats/components/EvaluationForm/useEvaluationFormForm'

import { useUpdateEvaluationFormTemplate } from './useUpdateEvaluationFormTemplate'

interface Props {
  onClose: () => void
  evaluationFormId: number
}

export const EditEvaluationFormTemplateSidepanel = ({ onClose, evaluationFormId }: Props) => {
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = React.useState(false)
  const { updateForm } = useUpdateEvaluationFormTemplate()

  const { form, hasInvalidQuestions } = useEvaluationFormForm({
    defaultValues: {
      name: '',
      questions: [buildEmptyQuestion()],
    },
    async onSubmit(data, { setErrors }) {
      const result = await updateForm({
        id: evaluationFormId,
        ...data,
      })
      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.evaluation_forms.edit.success_message'),
        })
        onClose()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ats.evaluation_forms.edit.error_message'),
        })
      }

      return handled
    },
  })

  const onCancelClicked = () => {
    form.dirty ? setUnsavedChangesModalOpen(true) : onClose()
  }

  return (
    <FormikProvider value={form}>
      <Sidepanel
        open
        width="wide"
        title={i18n.t('ats.evaluation_forms.edit.title')}
        footer={
          <Box flexDirection="row" justifyContent="spaceBetween" width="full" alignItems="center">
            {form.dirty ? (
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Icon.Medium icon="alert-circle-line" color="grey800" />
                <Text.Normal size="200">{i18n.t('ats.evaluation_forms.form.warning')}</Text.Normal>
              </Box>
            ) : (
              <div />
            )}

            <Box flexDirection="row" gap="s8" alignSelf="flexEnd">
              <Button
                onClick={onCancelClicked}
                type="secondary"
                aria-label={i18n.t('ats.evaluation_forms.form.cancel')}
              >
                {i18n.t('common.cancel')}
              </Button>
              <Button
                onClick={() => form.handleSubmit()}
                aria-label={i18n.t('ats.evaluation_forms.form.save')}
                disabled={form.isSubmitting}
              >
                {i18n.t('common.save')}
              </Button>
            </Box>
          </Box>
        }
        onClose={onClose}
        closeLabel={i18n.t('common.close')}
      >
        <Box gap="s16">
          <Alert>{i18n.t('ats.evaluation_forms.edit.info')}</Alert>
          <EvaluationFormEditor
            evaluationFormId={evaluationFormId}
            hasInvalidQuestions={hasInvalidQuestions}
          />
        </Box>

        {unsavedChangesModalOpen && (
          <Modal
            illustration="alert"
            title={i18n.t('ats.evaluation_forms.form.unsaved_changes.title')}
            description={i18n.t('ats.evaluation_forms.form.unsaved_changes.description')}
            onClose={() => setUnsavedChangesModalOpen(false)}
            onSubmit={onClose}
            footer={
              <Box flexDirection="row" gap="s16">
                <Button type="negative" submit>
                  {i18n.t('common.cancel')}
                </Button>
                <Button onClick={() => setUnsavedChangesModalOpen(false)}>
                  {i18n.t('ats.evaluation_forms.form.unsaved_changes.continue')}
                </Button>
              </Box>
            }
          />
        )}
      </Sidepanel>
    </FormikProvider>
  )
}
